import { useState, useEffect, useCallback } from "react";
import { api } from "../App";
import { getApiPrefix } from "../utils/apiHelper";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SearchableSelect } from "../components/ui/searchable-select";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import DataTable from "../components/ui/data-table";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { PageLoader } from "../components/ui/page-loader";
import { Loader2, CheckCircle, Clock, AlertCircle, Building2, Car, FileText, User, ArrowRight } from "lucide-react";
import { useTranslation } from "../contexts/I18nContext";

export default function TasksPage() {
  const { t } = useTranslation();
  const [tasks, setTasks] = useState([]);
  const [createdTasks, setCreatedTasks] = useState([]);
  const [appartamenti, setAppartamenti] = useState([]);
  const [automezzi, setAutomezzi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("ricevuti");
  const [completeDialog, setCompleteDialog] = useState({ open: false, task: null });
  
  const [completeForm, setCompleteForm] = useState({
    target_type: "",
    target_id: "",
    note: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const [tasksRes, createdRes, appRes, autoRes] = await Promise.all([
        api.get(`${getApiPrefix()}/tasks`),
        api.get(`${getApiPrefix()}/tasks/created`),
        api.get(`${getApiPrefix()}/appartamenti`).catch(() => ({ data: [] })),
        api.get(`${getApiPrefix()}/automezzi`).catch(() => ({ data: [] }))
      ]);
      setTasks(tasksRes.data);
      setCreatedTasks(createdRes.data);
      setAppartamenti(appRes.data || []);
      setAutomezzi(autoRes.data || []);
    } catch (err) {
      handleApiError(err, t("errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const formatCurrency = (val) => {
    if (val == null) return "-";
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(val);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed': return <Badge className="bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-1" />{t('common.completed')}</Badge>;
      case 'pending': return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="h-3 w-3 mr-1" />{t('common.scheduled')}</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getTaskTypeBadge = (type) => {
    switch (type) {
      case 'appartamenti': return <Badge className="bg-blue-100 text-blue-700"><Building2 className="h-3 w-3 mr-1" />{t('apartments.title')}</Badge>;
      case 'automezzi': return <Badge className="bg-purple-100 text-purple-700"><Car className="h-3 w-3 mr-1" />{t('vehicles.title')}</Badge>;
      default: return <Badge variant="outline">{type}</Badge>;
    }
  };

  const openCompleteDialog = (task) => {
    setCompleteForm({
      target_type: task.task_type === 'appartamenti' ? 'appartamento' : 'automezzo',
      target_id: "",
      note: ""
    });
    setCompleteDialog({ open: true, task });
  };

  const handleComplete = async () => {
    if (!completeForm.target_id) {
      handleApiError({ message: "Seleziona un elemento" });
      return;
    }
    
    try {
      await api.put(`${getApiPrefix()}/tasks/${completeDialog.task.id}/complete`, completeForm);
      showSuccess(t("success.taskCompleted"));
      setCompleteDialog({ open: false, task: null });
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.errorCompletion"));
    }
  };

  const pendingTasks = tasks.filter(t => t.status === 'pending');
  const completedTasks = tasks.filter(t => t.status === 'completed');
  const appartamentiTasks = pendingTasks.filter(t => t.task_type === 'appartamenti');
  const automezziTasks = pendingTasks.filter(t => t.task_type === 'automezzi');

  const columnsRicevuti = [
    {
      key: 'invoice',
      label: 'Fattura',
      render: (_, row) => row.invoice_numero ? (
        <div>
          <div className="font-medium">{row.invoice_numero}</div>
          <div className="text-xs text-muted-foreground">{row.fornitore_cliente}</div>
        </div>
      ) : <span className="text-muted-foreground">-</span>
    },
    { key: 'importo', label: t('common.amount'), align: 'right', render: (v) => formatCurrency(v) },
    { key: 'task_type', label: t('common.type'), render: (v) => getTaskTypeBadge(v) },
    { key: 'descrizione', label: t('common.description'), render: (v) => v || '-' },
    { 
      key: 'created_by_nome', 
      label: 'Assegnato da',
      render: (v, row) => (
        <div className="flex items-center gap-1">
          <User className="h-3 w-3 text-muted-foreground" />
          <span>{v || row.created_by_email || 'N/D'}</span>
        </div>
      )
    },
    { key: 'status', label: t('common.status'), render: (v) => getStatusBadge(v) },
    {
      key: 'actions',
      label: t('common.actions'),
      align: 'right',
      sortable: false,
      filterable: false,
      exportable: false,
      render: (_, row) => row.status === 'pending' ? (
        <Button size="sm" onClick={() => openCompleteDialog(row)}>
          <ArrowRight className="h-4 w-4 mr-1" />{t('common.complete')}</Button>
      ) : (
        <span className="text-xs text-muted-foreground">
          {row.target_type}: {row.target_id?.substring(0, 8)}...
        </span>
      )
    }
  ];

  const columnsCreati = [
    {
      key: 'invoice',
      label: 'Fattura',
      render: (_, row) => row.invoice_numero ? (
        <div>
          <div className="font-medium">{row.invoice_numero}</div>
          <div className="text-xs text-muted-foreground">{row.fornitore_cliente}</div>
        </div>
      ) : <span className="text-muted-foreground">-</span>
    },
    { key: 'importo', label: t('common.amount'), align: 'right', render: (v) => formatCurrency(v) },
    { key: 'task_type', label: t('common.type'), render: (v) => getTaskTypeBadge(v) },
    { 
      key: 'assigned_to_nome', 
      label: 'Assegnato a',
      render: (v, row) => (
        <div className="flex items-center gap-1">
          <User className="h-3 w-3 text-muted-foreground" />
          <span>{v || row.assigned_to_email || 'N/D'}</span>
        </div>
      )
    },
    { key: 'status', label: t('common.status'), render: (v) => getStatusBadge(v) },
    { 
      key: 'completed_at', 
      label: 'Completato il',
      render: (v) => v ? new Date(v).toLocaleDateString('it-IT') : '-'
    }
  ];

  if (loading) {
    return <PageLoader variant="section" text={t("common.loading")} />;
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div>
        <h1 className="text-2xl font-bold">{t("tasks.title")}</h1>
        <p className="text-muted-foreground">Gestisci i compiti di allocazione assegnati</p>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Clock className="h-8 w-8 text-yellow-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">{t('common.scheduled')}</p>
                <p className="text-2xl font-bold">{pendingTasks.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Building2 className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">{t('apartments.title')}</p>
                <p className="text-2xl font-bold">{appartamentiTasks.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Car className="h-8 w-8 text-purple-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">{t('vehicles.title')}</p>
                <p className="text-2xl font-bold">{automezziTasks.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">Completati</p>
                <p className="text-2xl font-bold">{completedTasks.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="ricevuti">
            Compiti Ricevuti ({tasks.length})
          </TabsTrigger>
          <TabsTrigger value="creati">
            Compiti Creati ({createdTasks.length})
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="ricevuti">
          <Card>
            <CardHeader>
              <CardTitle>Compiti Assegnati a Me</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={columnsRicevuti}
                data={tasks}
                idField="id"
                emptyMessage="Nessun compito assegnato"
                exportFileName="compiti_ricevuti"
              />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="creati">
          <Card>
            <CardHeader>
              <CardTitle>Compiti Creati da Me</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={columnsCreati}
                data={createdTasks}
                idField="id"
                emptyMessage="Nessun compito creato"
                exportFileName="compiti_creati"
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Complete Task Dialog */}
      <Dialog open={completeDialog.open} onOpenChange={(open) => setCompleteDialog({ ...completeDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("tasks.completeTask")}</DialogTitle>
            <DialogDescription>
              {completeDialog.task && (
                <>Fattura: {completeDialog.task.invoice_numero} - {formatCurrency(completeDialog.task.importo)}</>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {completeDialog.task?.task_type === 'appartamenti' ? (
              <div className="space-y-2">
                <Label>Seleziona Appartamento</Label>
                <SearchableSelect
                  options={appartamenti.map(a => ({ value: a.id, label: `${a.nome || a.indirizzo}${a.centro_costo_nome ? ` (${a.centro_costo_nome})` : ""}` }))}
                  value={completeForm.target_id}
                  onValueChange={(v) => setCompleteForm({ ...completeForm, target_id: v })}
                  placeholder={t("common.selectPlaceholder")}
                  searchPlaceholder={t("employees.searchApartment")}
                  testId="select-appartamento"
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Seleziona Automezzo</Label>
                <SearchableSelect
                  options={automezzi.map(a => ({ value: a.id, label: `${a.marca} ${a.modello} - ${a.targa}${a.centro_costo_nome ? ` (${a.centro_costo_nome})` : ""}` }))}
                  value={completeForm.target_id}
                  onValueChange={(v) => setCompleteForm({ ...completeForm, target_id: v })}
                  placeholder={t("dialogs.selectVehicle")}
                  searchPlaceholder="Cerca automezzo..."
                  testId="select-automezzo"
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Note (opzionale)</Label>
              <Textarea 
                value={completeForm.note} 
                onChange={(e) => setCompleteForm({ ...completeForm, note: e.target.value })}
                placeholder="Aggiungi note..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteDialog({ open: false, task: null })}>{t("common.cancel")}</Button>
            <Button onClick={handleComplete}>
              <CheckCircle className="h-4 w-4 mr-2" />{t('common.complete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
